import { useState } from 'react';
import Sidebar from '../components/Sidebar';

interface PhotoFolder {
  id: string;
  customerName: string;
  weddingDate: string;
  shootDate: string;
  photographer: string;
  photoCount: number;
  status: 'uploading' | 'ready' | 'shared' | 'expired';
  downloadLink: string;
  expiryDate: string;
  downloadCount: number;
  maxDownloads: number;
  type: 'original' | 'retouched' | 'selection';
}

export default function StudioPhotos() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<PhotoFolder | null>(null);

  const [folders, setFolders] = useState<PhotoFolder[]>([
    {
      id: '1',
      customerName: '김민지',
      weddingDate: '2025-02-14',
      shootDate: '2025-01-10',
      photographer: '박작가',
      photoCount: 245,
      status: 'shared',
      downloadLink: 'https://photos.example.com/abc123',
      expiryDate: '2025-02-28',
      downloadCount: 3,
      maxDownloads: 5,
      type: 'retouched'
    },
    {
      id: '2',
      customerName: '이서연',
      weddingDate: '2025-03-20',
      shootDate: '2025-01-12',
      photographer: '최작가',
      photoCount: 180,
      status: 'ready',
      downloadLink: 'https://photos.example.com/def456',
      expiryDate: '2025-03-31',
      downloadCount: 0,
      maxDownloads: 3,
      type: 'original'
    },
    {
      id: '3',
      customerName: '정유진',
      weddingDate: '2025-04-05',
      shootDate: '2025-01-14',
      photographer: '김작가',
      photoCount: 320,
      status: 'uploading',
      downloadLink: '',
      expiryDate: '2025-04-30',
      downloadCount: 0,
      maxDownloads: 5,
      type: 'retouched'
    },
    {
      id: '4',
      customerName: '박지은',
      weddingDate: '2024-12-25',
      shootDate: '2024-12-10',
      photographer: '이작가',
      photoCount: 198,
      status: 'expired',
      downloadLink: 'https://photos.example.com/ghi789',
      expiryDate: '2025-01-10',
      downloadCount: 5,
      maxDownloads: 5,
      type: 'original'
    },
  ]);

  const [newFolder, setNewFolder] = useState({
    customerName: '',
    weddingDate: '',
    shootDate: '',
    photographer: '',
    photoCount: 0,
    expiryDate: '',
    maxDownloads: 3,
    type: 'original' as 'original' | 'retouched' | 'selection'
  });

  const filteredFolders = folders.filter(folder => {
    const matchesSearch = folder.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         folder.photographer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || folder.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'uploading': return 'bg-yellow-100 text-yellow-800';
      case 'ready': return 'bg-blue-100 text-blue-800';
      case 'shared': return 'bg-green-100 text-green-800';
      case 'expired': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'uploading': return '업로드 중';
      case 'ready': return '준비 완료';
      case 'shared': return '공유 중';
      case 'expired': return '만료됨';
      default: return status;
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case 'original': return '원본';
      case 'retouched': return '리터칭본';
      case 'selection': return '선택본';
      default: return type;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'original': return 'bg-purple-100 text-purple-800';
      case 'retouched': return 'bg-pink-100 text-pink-800';
      case 'selection': return 'bg-teal-100 text-teal-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleCreateFolder = () => {
    if (newFolder.customerName && newFolder.shootDate && newFolder.photographer) {
      const folder: PhotoFolder = {
        id: Date.now().toString(),
        customerName: newFolder.customerName,
        weddingDate: newFolder.weddingDate,
        shootDate: newFolder.shootDate,
        photographer: newFolder.photographer,
        photoCount: newFolder.photoCount,
        status: 'uploading',
        downloadLink: '',
        expiryDate: newFolder.expiryDate,
        downloadCount: 0,
        maxDownloads: newFolder.maxDownloads,
        type: newFolder.type
      };
      setFolders([...folders, folder]);
      setNewFolder({
        customerName: '',
        weddingDate: '',
        shootDate: '',
        photographer: '',
        photoCount: 0,
        expiryDate: '',
        maxDownloads: 3,
        type: 'original'
      });
      setShowUploadModal(false);
    }
  };

  const handleGenerateLink = (folder: PhotoFolder) => {
    const link = `https://photos.example.com/${Math.random().toString(36).substr(2, 9)}`;
    const updatedFolders = folders.map(f => 
      f.id === folder.id 
        ? { ...f, downloadLink: link, status: 'shared' as const }
        : f
    );
    setFolders(updatedFolders);
    setSelectedFolder({ ...folder, downloadLink: link, status: 'shared' });
    setShowShareModal(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('링크가 복사되었습니다!');
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">촬영 사진 관리</h1>
              <p className="text-sm text-gray-600 mt-1">고객별 사진 디렉토리 관리 및 공유</p>
            </div>
            <button
              onClick={() => setShowUploadModal(true)}
              className="bg-blue-600 text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap flex items-center gap-2 cursor-pointer"
            >
              <i className="ri-upload-cloud-line"></i>
              사진 업로드
            </button>
          </div>

          {/* 검색 및 필터 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">검색</label>
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="고객명 또는 작가명 검색"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">상태 필터</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">전체</option>
                  <option value="uploading">업로드 중</option>
                  <option value="ready">준비 완료</option>
                  <option value="shared">공유 중</option>
                  <option value="expired">만료됨</option>
                </select>
              </div>
            </div>
          </div>

          {/* 사진 폴더 목록 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredFolders.map(folder => (
              <div key={folder.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">{folder.customerName}</h3>
                    <p className="text-sm text-gray-600 mt-1">촬영일: {folder.shootDate}</p>
                    {folder.weddingDate && (
                      <p className="text-sm text-gray-600">예식일: {folder.weddingDate}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(folder.status)}`}>
                      {getStatusText(folder.status)}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(folder.type)}`}>
                      {getTypeText(folder.type)}
                    </span>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-700">
                    <i className="ri-camera-line w-5 h-5 flex items-center justify-center mr-2 text-gray-500"></i>
                    <span>작가: {folder.photographer}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-700">
                    <i className="ri-image-line w-5 h-5 flex items-center justify-center mr-2 text-gray-500"></i>
                    <span>사진 수: {folder.photoCount}장</span>
                  </div>
                  {folder.downloadLink && (
                    <>
                      <div className="flex items-center text-sm text-gray-700">
                        <i className="ri-download-line w-5 h-5 flex items-center justify-center mr-2 text-gray-500"></i>
                        <span>다운로드: {folder.downloadCount} / {folder.maxDownloads}회</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-700">
                        <i className="ri-calendar-line w-5 h-5 flex items-center justify-center mr-2 text-gray-500"></i>
                        <span>만료일: {folder.expiryDate}</span>
                      </div>
                    </>
                  )}
                </div>

                {folder.downloadLink && (
                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <p className="text-xs text-gray-600 mb-1">다운로드 링크</p>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={folder.downloadLink}
                        readOnly
                        className="flex-1 text-sm bg-white px-3 py-1.5 border border-gray-300 rounded"
                      />
                      <button
                        onClick={() => copyToClipboard(folder.downloadLink)}
                        className="text-blue-600 hover:text-blue-700 cursor-pointer"
                      >
                        <i className="ri-file-copy-line text-lg"></i>
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  {!folder.downloadLink && folder.status === 'ready' && (
                    <button
                      onClick={() => handleGenerateLink(folder)}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
                    >
                      <i className="ri-share-line mr-2"></i>
                      링크 생성
                    </button>
                  )}
                  {folder.downloadLink && (
                    <button
                      onClick={() => {
                        setSelectedFolder(folder);
                        setShowShareModal(true);
                      }}
                      className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
                    >
                      <i className="ri-share-line mr-2"></i>
                      공유 정보
                    </button>
                  )}
                  <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer text-sm">
                    <i className="ri-more-line"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredFolders.length === 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <i className="ri-folder-image-line text-5xl text-gray-300 mb-4"></i>
              <p className="text-gray-600">검색 결과가 없습니다</p>
            </div>
          )}
        </div>
      </div>

      {/* 사진 업로드 모달 */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-4">사진 업로드</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">고객명</label>
                <input
                  type="text"
                  value={newFolder.customerName}
                  onChange={(e) => setNewFolder({ ...newFolder, customerName: e.target.value })}
                  placeholder="고객 이름"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">촬영일</label>
                <input
                  type="date"
                  value={newFolder.shootDate}
                  onChange={(e) => setNewFolder({ ...newFolder, shootDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">예식일 (선택)</label>
                <input
                  type="date"
                  value={newFolder.weddingDate}
                  onChange={(e) => setNewFolder({ ...newFolder, weddingDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">담당 작가</label>
                <input
                  type="text"
                  value={newFolder.photographer}
                  onChange={(e) => setNewFolder({ ...newFolder, photographer: e.target.value })}
                  placeholder="작가 이름"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">사진 타입</label>
                <select
                  value={newFolder.type}
                  onChange={(e) => setNewFolder({ ...newFolder, type: e.target.value as 'original' | 'retouched' | 'selection' })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="original">원본</option>
                  <option value="retouched">리터칭본</option>
                  <option value="selection">선택본</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">사진 수량</label>
                <input
                  type="number"
                  value={newFolder.photoCount}
                  onChange={(e) => setNewFolder({ ...newFolder, photoCount: parseInt(e.target.value) || 0 })}
                  min="0"
                  placeholder="사진 장수"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">만료일</label>
                <input
                  type="date"
                  value={newFolder.expiryDate}
                  onChange={(e) => setNewFolder({ ...newFolder, expiryDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">최대 다운로드 횟수</label>
                <input
                  type="number"
                  value={newFolder.maxDownloads}
                  onChange={(e) => setNewFolder({ ...newFolder, maxDownloads: parseInt(e.target.value) || 3 })}
                  min="1"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowUploadModal(false)}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                취소
              </button>
              <button
                onClick={handleCreateFolder}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                생성
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 공유 정보 모달 */}
      {showShareModal && selectedFolder && (
        <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">공유 정보</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">고객명</label>
                <p className="text-gray-900">{selectedFolder.customerName}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">다운로드 링크</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={selectedFolder.downloadLink}
                    readOnly
                    className="flex-1 px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm"
                  />
                  <button
                    onClick={() => copyToClipboard(selectedFolder.downloadLink)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    복사
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">다운로드 횟수</label>
                  <p className="text-gray-900">{selectedFolder.downloadCount} / {selectedFolder.maxDownloads}회</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">만료일</label>
                  <p className="text-gray-900">{selectedFolder.expiryDate}</p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <i className="ri-information-line mr-2"></i>
                  이 링크는 {selectedFolder.expiryDate}까지 유효하며, 최대 {selectedFolder.maxDownloads}회까지 다운로드 가능합니다.
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowShareModal(false)}
                className="flex-1 px-4 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
